'use client';

import { useParams } from 'next/navigation';
import { 
  AnalyticsEventNames,
  BaseAnalyticsEvent,
  PlaylistGeneratorAnalyticsEvent,
  FilterAnalyticsEvent,
  SpotifyAnalyticsEvent,
  ContentAnalyticsEvent,
  NavigationAnalyticsEvent,
  AnalyticsContext
} from '@/lib/data/types/analytics';
import { 
  captureAnalyticsEvent, 
  getAnalyticsSessionId, 
  getAnalyticsUserId 
} from './posthog';

/**
 * Analytics utilities for MusicMagic.io
 * Provides consistent event tracking across the application
 * 
 * DUAL TRACKING APPROACH:
 * 1. PostHog automatically captures $pageview and $pageleave events (basic metrics)
 * 2. Custom structured events provide detailed funnel analysis and context
 * 
 * This gives us comprehensive analytics coverage:
 * - Basic metrics: bounce rate, time on page, page views (via PostHog automatic events)
 * - Funnel analysis: structured page categories and user journey tracking (via custom events)
 * - Interaction tracking: playlist generator, filtering, auth flows (via custom events)
 */

/**
 * Get base analytics properties for all events
 */
function getBaseEventProperties(): Omit<BaseAnalyticsEvent, 'session_id' | 'user_id'> {
  const timestamp = Date.now();
  const page_url = typeof window !== 'undefined' ? window.location.href : '';
  const referrer = typeof window !== 'undefined' ? document.referrer : undefined;
  const user_agent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const locale = typeof window !== 'undefined' ? 
    window.location.pathname.split('/')[1] || 'en' : 'en';

  return {
    timestamp,
    page_url,
    referrer,
    user_agent,
    locale,
  };
}

/**
 * Get complete analytics context
 */
export function getAnalyticsContext(): AnalyticsContext {
  const baseProps = getBaseEventProperties();
  
  return {
    isEnabled: typeof window !== 'undefined',
    userId: getAnalyticsUserId(),
    sessionId: getAnalyticsSessionId(),
    locale: baseProps.locale,
    pageUrl: baseProps.page_url,
    referrer: baseProps.referrer,
  };
}

/**
 * Track page view events
 */
export function trackPageView(
  pageName: string,
  additionalProperties?: Record<string, any>
): void {
  const baseProps = getBaseEventProperties();
  
  captureAnalyticsEvent(AnalyticsEventNames.PAGE_VIEWED, {
    ...baseProps,
    session_id: getAnalyticsSessionId(),
    user_id: getAnalyticsUserId(),
    page_name: pageName,
    ...additionalProperties,
  });
}

/**
 * Track playlist generator events
 */
export function trackPlaylistGeneratorEvent(
  eventName: string,
  properties: Partial<PlaylistGeneratorAnalyticsEvent>
): void {
  const baseProps = getBaseEventProperties();
  
  captureAnalyticsEvent(eventName, {
    ...baseProps,
    session_id: getAnalyticsSessionId(),
    user_id: getAnalyticsUserId(),
    ...properties,
  });
}

/**
 * Track filter and search events
 */
export function trackFilterEvent(
  eventName: string,
  properties: Partial<FilterAnalyticsEvent>
): void {
  const baseProps = getBaseEventProperties();
  
  captureAnalyticsEvent(eventName, {
    ...baseProps,
    session_id: getAnalyticsSessionId(),
    user_id: getAnalyticsUserId(),
    ...properties,
  });
}

/**
 * Track Spotify integration events
 */
export function trackSpotifyEvent(
  eventName: string,
  properties: Partial<SpotifyAnalyticsEvent>
): void {
  const baseProps = getBaseEventProperties();
  
  captureAnalyticsEvent(eventName, {
    ...baseProps,
    session_id: getAnalyticsSessionId(),
    user_id: getAnalyticsUserId(),
    ...properties,
  });
}

/**
 * Track content engagement events
 */
export function trackContentEvent(
  eventName: string,
  properties: Partial<ContentAnalyticsEvent>
): void {
  const baseProps = getBaseEventProperties();
  
  captureAnalyticsEvent(eventName, {
    ...baseProps,
    session_id: getAnalyticsSessionId(),
    user_id: getAnalyticsUserId(),
    ...properties,
  });
}

/**
 * Track navigation events
 */
export function trackNavigationEvent(
  eventName: string,
  properties: Partial<NavigationAnalyticsEvent>
): void {
  const baseProps = getBaseEventProperties();
  
  captureAnalyticsEvent(eventName, {
    ...baseProps,
    session_id: getAnalyticsSessionId(),
    user_id: getAnalyticsUserId(),
    ...properties,
  });
}

/**
 * Specific tracking functions for common events
 */

// Playlist Generator Events
export function trackPlaylistGeneratorViewed(
  step: PlaylistGeneratorAnalyticsEvent['step'] = 'entry'
): void {
  trackPlaylistGeneratorEvent(AnalyticsEventNames.PLAYLIST_GENERATOR_VIEWED, {
    step,
    config_completeness: 0,
  });
}

export function trackAccountSelected(
  accountSlug: string,
  selectedAccounts: string[],
  totalTracks?: number
): void {
  trackPlaylistGeneratorEvent(AnalyticsEventNames.ACCOUNT_SELECTED, {
    step: 'config',
    selected_accounts: selectedAccounts,
    total_tracks: totalTracks,
    config_completeness: calculateConfigCompleteness(selectedAccounts, []),
  });
}

export function trackGenreSelected(
  genreSlug: string,
  selectedGenres: string[],
  selectedAccounts: string[],
  totalTracks?: number
): void {
  trackPlaylistGeneratorEvent(AnalyticsEventNames.GENRE_SELECTED, {
    step: 'config',
    selected_genres: selectedGenres,
    selected_accounts: selectedAccounts,
    total_tracks: totalTracks,
    config_completeness: calculateConfigCompleteness(selectedAccounts, selectedGenres),
  });
}

export function trackToggleChanged(
  toggleType: 'recently_discovered' | 'most_popular',
  value: boolean,
  selectedAccounts: string[],
  selectedGenres: string[]
): void {
  trackPlaylistGeneratorEvent(AnalyticsEventNames.TOGGLE_CHANGED, {
    step: 'config',
    selected_accounts: selectedAccounts,
    selected_genres: selectedGenres,
    recently_discovered: toggleType === 'recently_discovered' ? value : undefined,
    most_popular: toggleType === 'most_popular' ? value : undefined,
    config_completeness: calculateConfigCompleteness(selectedAccounts, selectedGenres),
  });
}

export function trackValenceAdjusted(
  valence: number,
  selectedAccounts: string[],
  selectedGenres: string[]
): void {
  trackPlaylistGeneratorEvent(AnalyticsEventNames.VALENCE_ADJUSTED, {
    step: 'config',
    valence,
    selected_accounts: selectedAccounts,
    selected_genres: selectedGenres,
    config_completeness: calculateConfigCompleteness(selectedAccounts, selectedGenres),
  });
}

export function trackBuildPlaylistClicked(
  selectedAccounts: string[],
  selectedGenres: string[],
  recentlyDiscovered: boolean,
  mostPopular: boolean,
  valence: number,
  totalTracks?: number
): void {
  trackPlaylistGeneratorEvent(AnalyticsEventNames.BUILD_PLAYLIST_CLICKED, {
    step: 'config',
    selected_accounts: selectedAccounts,
    selected_genres: selectedGenres,
    recently_discovered: recentlyDiscovered,
    most_popular: mostPopular,
    valence,
    total_tracks: totalTracks,
    config_completeness: calculateConfigCompleteness(selectedAccounts, selectedGenres),
  });
}

export function trackPlaylistPreviewViewed(playlistUuid: string): void {
  trackPlaylistGeneratorEvent(AnalyticsEventNames.PLAYLIST_PREVIEW_VIEWED, {
    step: 'preview',
  });
}

// Spotify Events
export function trackSpotifyAuthStarted(
  source: SpotifyAnalyticsEvent['auth_source']
): void {
  trackSpotifyEvent(AnalyticsEventNames.SPOTIFY_AUTH_STARTED, {
    auth_source: source,
  });
}

export function trackSpotifyAuthCompleted(
  source: SpotifyAnalyticsEvent['auth_source'],
  duration?: number
): void {
  trackSpotifyEvent(AnalyticsEventNames.SPOTIFY_AUTH_COMPLETED, {
    auth_source: source,
    auth_duration: duration,
  });
}

export function trackSpotifyAddAttempted(
  playlistUuid: string,
  source: SpotifyAnalyticsEvent['auth_source']
): void {
  trackSpotifyEvent(AnalyticsEventNames.SPOTIFY_ADD_ATTEMPTED, {
    playlist_uuid: playlistUuid,
    auth_source: source,
  });
}

export function trackSpotifyAddCompleted(
  playlistUuid: string,
  spotifyPlaylistId: string,
  source: SpotifyAnalyticsEvent['auth_source']
): void {
  trackSpotifyEvent(AnalyticsEventNames.SPOTIFY_ADD_COMPLETED, {
    playlist_uuid: playlistUuid,
    spotify_playlist_id: spotifyPlaylistId,
    auth_source: source,
  });
}

// Filter Events
export function trackFilterApplied(
  filterType: FilterAnalyticsEvent['filter_type'],
  filterValue: string,
  filterSlug: string,
  activeFilters: FilterAnalyticsEvent['active_filters'],
  resultsCount: number,
  pageNumber: number = 1
): void {
  trackFilterEvent(AnalyticsEventNames.FILTER_APPLIED, {
    filter_type: filterType,
    filter_value: filterValue,
    filter_slug: filterSlug,
    active_filters: activeFilters,
    results_count: resultsCount,
    page_number: pageNumber,
  });
}

export function trackFilterRemoved(
  filterType: FilterAnalyticsEvent['filter_type'],
  filterValue: string,
  filterSlug: string,
  activeFilters: FilterAnalyticsEvent['active_filters'],
  resultsCount: number,
  pageNumber: number = 1
): void {
  trackFilterEvent(AnalyticsEventNames.FILTER_REMOVED, {
    filter_type: filterType,
    filter_value: filterValue,
    filter_slug: filterSlug,
    active_filters: activeFilters,
    results_count: resultsCount,
    page_number: pageNumber,
  });
}

export function trackSearchPerformed(
  searchQuery: string,
  resultsCount: number,
  activeFilters: FilterAnalyticsEvent['active_filters']
): void {
  trackFilterEvent(AnalyticsEventNames.SEARCH_PERFORMED, {
    filter_type: 'search',
    filter_value: searchQuery,
    filter_slug: 'search',
    active_filters: activeFilters,
    results_count: resultsCount,
    page_number: 1,
  });
}

// Content Events
export function trackPlaylistViewed(
  playlistSlug: string,
  playlistTitle?: string,
  position?: number
): void {
  trackContentEvent(AnalyticsEventNames.PLAYLIST_VIEWED, {
    content_type: 'playlist',
    content_slug: playlistSlug,
    content_title: playlistTitle,
    engagement_type: 'view',
    position,
  });
}

// Navigation Events
export function trackNavigationClicked(
  destinationUrl: string,
  navigationType: NavigationAnalyticsEvent['navigation_type'],
  linkText?: string,
  sourceSection?: string
): void {
  trackNavigationEvent(AnalyticsEventNames.NAVIGATION_CLICKED, {
    navigation_type: navigationType,
    destination_url: destinationUrl,
    link_text: linkText,
    source_section: sourceSection,
  });
}

export function trackPaginationClicked(
  destinationUrl: string,
  pageNumber: number,
  totalPages: number
): void {
  trackNavigationEvent(AnalyticsEventNames.PAGINATION_CLICKED, {
    navigation_type: 'pagination',
    destination_url: destinationUrl,
    link_text: `Page ${pageNumber}/${totalPages}`,
  });
}

/**
 * Helper function to calculate configuration completeness
 */
function calculateConfigCompleteness(
  selectedAccounts: string[],
  selectedGenres: string[]
): number {
  const hasAccounts = selectedAccounts.length > 0;
  const hasGenres = selectedGenres.length > 0;
  
  if (hasAccounts && hasGenres) return 1.0;
  if (hasAccounts || hasGenres) return 0.5;
  return 0;
} 